import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Paper,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmptyReportSection from './EmptyReportSection';
import GroupDetailsFailuresGroupedTable from './GroupDetailsFailuresGroupedTable';
import { numberOrZero } from './aiReportUtils';
import {
  formatRepeatedFailuresGroupedTableValue,
  getRepeatedFailuresGroupedTableAttributes,
  getRepeatedFailuresGroupedTableFlows,
  getRepeatedFailuresGroupedTableGroupLabel,
} from './repeatedFailuresGroupedTableUtils';
import { reportStyles } from './styles';

const getGroupKey = (group, index) =>
  `${group.type || 'group'}-${getRepeatedFailuresGroupedTableGroupLabel(group)}-${index}`;

export default function RepeatedFailuresGroupedTable({ repeatedFailuresGrouped = [] }) {
  return (
    <Paper variant="outlined" sx={reportStyles.reportPaperWithMargin}>
      <Accordion disableGutters elevation={0}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box>
            <Typography variant="subtitle1" sx={reportStyles.subsectionTitle}>
              Repeated Failures Grouped By Event
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Repeated events and attributes across flows, with expandable flow coverage.
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {repeatedFailuresGrouped.length === 0 ? (
            <EmptyReportSection>No grouped repeated failures reported.</EmptyReportSection>
          ) : (
            <Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr',
                  gap: 2,
                  px: 2,
                  py: 1,
                }}
              >
                <Typography variant="body2" sx={reportStyles.tableHeaderCell}>
                  Event / Attribute
                </Typography>
                <Typography variant="body2" align="right" sx={reportStyles.tableHeaderCell}>
                  Flow Count
                </Typography>
                <Typography variant="body2" align="right" sx={reportStyles.tableHeaderCell}>
                  Attributes
                </Typography>
              </Box>

              {repeatedFailuresGrouped.map((group, index) => {
                const groupKey = getGroupKey(group, index);
                const groupFlows = getRepeatedFailuresGroupedTableFlows(group);
                const groupAttributes = getRepeatedFailuresGroupedTableAttributes(group);

                return (
                  <Accordion
                    key={groupKey}
                    disableGutters
                    elevation={0}
                    sx={{
                      mb: 1,
                      '&:before': {
                        display: 'none',
                      },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '2fr 1fr 1fr',
                          alignItems: 'center',
                          gap: 2,
                          width: '100%',
                          pr: 1,
                        }}
                      >
                        <Typography variant="body2" sx={reportStyles.wordBreakCell}>
                          {formatRepeatedFailuresGroupedTableValue(
                            getRepeatedFailuresGroupedTableGroupLabel(group),
                          )}
                        </Typography>
                        <Typography variant="body2" align="right">
                          {numberOrZero(group.flow_count)}
                        </Typography>
                        <Typography variant="body2" align="right">
                          {groupAttributes.length}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <GroupDetailsFailuresGroupedTable
                        groupKey={groupKey}
                        flows={groupFlows}
                        attributes={groupAttributes}
                      />
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
}
